import { Injectable, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';

export interface FeedbackThread {
  id: string; user_id: string; kind: 'suggestion' | 'error' | 'other'; subject: string; body: string;
  status: 'new' | 'processing' | 'resolved' | 'declined'; created_at: string;
  admin_seen_at: string | null; user_seen_at: string | null; last_reply_at: string | null;
  sender: { full_name: string | null; username: string | null } | null;
}
export interface FeedbackReply {
  id: string;
  body: string;
  created_at: string;
  author: { full_name: string | null; username: string | null } | null;
}

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  private supabase = inject(SupabaseService);
  readonly auth = inject(AuthService);
  threads = signal<FeedbackThread[]>([]);
  error = signal(false);
  loading = signal(false);
  adminUnread = computed(() => this.threads().filter(t => !t.admin_seen_at).length);
  userUnread = computed(() => this.threads().filter(t => t.user_id === this.auth.currentUser()?.id && t.last_reply_at && (!t.user_seen_at || t.last_reply_at > t.user_seen_at)).length);
  constructor() {
    effect(() => { this.auth.currentUser(); this.auth.isAdmin(); void this.refresh(); });
    const timer = setInterval(() => { if (this.auth.isAuthenticated()) void this.refresh(); }, 30000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }
  private generation = 0;
  async refresh() {
    const generation = ++this.generation;
    if (!this.auth.currentUser() || this.auth.currentUser()?.is_anonymous) { this.threads.set([]); this.loading.set(false); this.error.set(false); return; }
    this.loading.set(true);
    try {
      const rows: FeedbackThread[] = [];
      for (let offset = 0; ; offset += 1000) {
        const { data, error } = await this.supabase.client.from('feedback_threads')
          .select('*, sender:profiles!user_id(full_name,username)').order('created_at', { ascending: false }).order('id').range(offset, offset + 999);
        if (error) throw error;
        rows.push(...(data as unknown as FeedbackThread[] || []));
        if (!data || data.length < 1000) break;
      }
      if (generation === this.generation) { this.threads.set(rows); this.error.set(false); }
    } catch { if (generation === this.generation) this.error.set(true); }
    finally { if (generation === this.generation) this.loading.set(false); }
  }
  async send(kind: string, subject: string, body: string) {
    const { error } = await this.supabase.client.from('feedback_threads').insert({ user_id: this.auth.currentUser()?.id, kind, subject: subject.trim(), body: body.trim() });
    if (error) throw error;
    await this.refresh();
  }
  async replies(id: string): Promise<FeedbackReply[]> {
    const rows: FeedbackReply[] = [];
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await this.supabase.client.from('feedback_replies')
        .select('id,body,created_at,author:profiles!feedback_replies_author_id_fkey(full_name,username)')
        .eq('thread_id', id).order('created_at').order('id').range(offset, offset + 999);
      if (error) throw error;
      rows.push(...(data as unknown as FeedbackReply[] || []));
      if (!data || data.length < 1000) return rows;
    }
  }
  async markRead(id: string, admin: boolean) {
    const { error } = await this.supabase.client.from('feedback_threads')
      .update({ [admin ? 'admin_seen_at' : 'user_seen_at']: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    await this.refresh();
  }
  async reply(id: string, body: string, status: string) {
    const { error } = await this.supabase.client.rpc('reply_to_feedback', { p_thread_id: id, p_body: body.trim(), p_status: status });
    if (error) throw error;
    await this.refresh();
  }
  async remove(id: string) {
    const { error } = await this.supabase.client.from('feedback_threads')
      .delete().eq('id', id).select('id').single();
    if (error) throw error;
    // Ignore older refreshes that could otherwise put the deleted thread back.
    ++this.generation;
    this.threads.update(threads => threads.filter(thread => thread.id !== id));
    await this.refresh();
  }
}
